
import { computed, markRaw, nextTick, onMounted, ref, watch } from "vue";
import QuestionnaireResultWrapper from "@/views/pages/questionnaire/components/QuestionnaireResultWrapper.vue";
import { configure, ErrorMessage, Field, Form } from "vee-validate";
import * as yup from "yup";
import useLocale from "@/core/common/useLocale";
import { useRouter } from "vue-router";
import { useGtm } from "@gtm-support/vue-gtm";
import { useStore } from "vuex";
import { Actions, Mutations } from "@/store/enums/QuestionnaireStoreEnums";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import * as Yup from "yup";
import { PasswordMeterComponent } from "@/assets/ts/components";
import { reinitializeComponents } from "@/core/plugins/keenthemes";
import NoMedicalRecordsForm from "@/views/pages/questionnaire/components/NoMedicalRecordsForm.vue";
import QuestionnaireSignUp from "@/views/pages/questionnaire/components/QuestionnaireSignUp.vue";

export default {
  name: "QuestionnaireMedicalRecords",
  components: {
    QuestionnaireSignUp,
    NoMedicalRecordsForm,
    QuestionnaireResultWrapper,
  },
  

  setup() {
    const { t } = useLocale();
    const gtm = useGtm();
    const selectedStatus = ref();
    const hospitalName = ref("");
    const cityState = ref("");
    const physicianName = ref("");
    const router = useRouter();
    const store = useStore();
    const userEmail = ref(store.getters.userEmail);
    const userPassword = ref();
    const isPasswordHidden = ref(true);
    const isConfirmationPasswordHidden = ref(true);
    const disableUserInputEmail = ref(false);
    const noMedicalRecordsStep = ref(1);
    const description = ref(t('In order to find clinical trials that precisely match your condition we will need your medical records.'));

    watch(selectedStatus, (newValue) => {
  if (newValue === 'has_records' || newValue === 'no_records') {
    description.value = t('You can track your results by signing up to our patient platform.');
  } else {
    description.value = t('In order to find clinical trials that precisely match your condition we will need your medical records.');
  }
});
    onMounted(() => {
      gtm?.trackEvent({
        event: "questionnaire_medical_record_page_view",
        event_name: "questionnaire_medical_record_page_view",
        category: "Custom Events",
      });

      nextTick(() => {
        PasswordMeterComponent.bootstrap();

        nextTick(() => {
          reinitializeComponents();
        });
      });

      if (userEmail.value) {
        disableUserInputEmail.value = true;
      }
    });

    const togglePassword = function (type) {
      if (type === "password") {
        isPasswordHidden.value = !isPasswordHidden.value;
      } else if (type === "confirmation_password") {
        isConfirmationPasswordHidden.value =
          !isConfirmationPasswordHidden.value;
      }
    };

    const submitPhysicianForm = async function (payload) {
      gtm?.trackEvent({
        event: "questionnaire_physician_save",
        event_name: "questionnaire_physician_save",
        category: "Custom Events",
      });

      try {
        await store.dispatch(Actions.POST_PHYSICIAN_FORM, {
          ...payload,
        });

        if (physicianFormSavedStatus.value) {
          Swal.fire({
            title: t("Thank you!"),
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: t("Next"),
            customClass: {
              confirmButton: "swal-custom-btn-confirm",
            },
          }).then(() => {
            noMedicalRecordsStep.value++;
          });
        } else {
          Swal.fire({
            title: "We are unable to complete your process...",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: t("Try again!"),
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        console.error(e);
      }
    };

    const isFormFilled = computed(() => {
      return !!(hospitalName.value && cityState.value && physicianName.value);
    });

    const handleUploadMedicalRecords = function () {
      gtm?.trackEvent({
        event: "questionnaire_upload_medical_record",
        event_name: "questionnaire_upload_medical_record",
        category: "Custom Events",
      });

      router.push({ name: "sign-up" });
    };

    const validationSchema = yup.object({
      hospital_name: yup
        .string()
        .matches(
          /^([^0-9+-]*)$/,
          t("Only alphabets are allowed for this field")
        )
        .required(t("Hospital name is a required field"))
        .label(t("Hospital name")),
      hospital_location: yup
        .string()
        .required(t("City, State is a required field"))
        .label(t("City, State")),
      physician_name: yup
        .string()
        .matches(
          /^([^0-9+-]*)$/,
          t("Only alphabets are allowed for this field")
        )
        .required(t("Physician Name is a required field"))
        .label(t("Physician Name")),
    });

    const registrationSchema = Yup.object().shape({
      email: Yup.string()
        .min(4)
        .required(t("Email is a required field"))
        .email(t("emailValidation"))
        .label(t("Email")),
      password: Yup.string()
        .matches(/[1-9]+/, t("Use number"))
        .matches(/[a-z]+/, t("Use lowercase character"))
        .matches(/[A-Z]+/, t("Use uppercase character"))
        .matches(
          /[!"#$%&'()*+,-./:;<=>?@[\]^_'{|}~]+/,
          t("Use special character")
        )
        .min(8, t("Password must be at least 8 characters"))
        .required(t("Password is a required field"))
        .label(t("Password")),
      password_confirmation: Yup.string()
        .required(t("Password is a required field"))
        .oneOf(
          [Yup.ref("password"), null],
          t("The password and its confirm are not the same")
        )
        .label(t("Password Confirmation")),
    });

    const physicianFormSavedStatus = computed(() => {
      return store.getters.physicianFormSavedStatus;
    });

    const onSubmitRegister = async (values) => {
      store.commit(Mutations.SET_AUTH_STATUS, false);

      await store.dispatch(Actions.CREATE_PLATFORM_USER, values);

      if (store.getters.isSignedUpFromQuestionnaire) {
        Swal.fire({
          title: t("Thank you!"),
          icon: "success",
          buttonsStyling: false,
          confirmButtonText:
            selectedStatus.value === "has_records"
              ? t("Go To Medical Records Page")
              : t("Go to Patient Platform"),
          customClass: {
            confirmButton: "swal-custom-btn-confirm",
          },
        }).then(() => {
          if (selectedStatus.value === "has_records") {
            router.push({ name: "medical-records" });
          } else {
            router.push({ name: "home" });
          }
        });
      } else {
        Swal.fire({
          title: t("Looks like you already signed to our Patient Platform."),
          icon: "warning",
          buttonsStyling: false,
          confirmButtonText: t("Sign In"),
          customClass: {
            confirmButton: "swal-custom-btn-confirm",
          },
        }).then(() => {
          router.push({ name: "sign-in" });
        });
      }
    };

    const noMedicalRecordsStepComponentMap = {
      1: {
        component: markRaw(NoMedicalRecordsForm),
        event: { submitForm: submitPhysicianForm },
      },
      2: {
        component: markRaw(QuestionnaireSignUp),
        event: { submitForm: onSubmitRegister },
      },
    };

    const noMedicalRecordContent = computed(() => {
      return noMedicalRecordsStepComponentMap[noMedicalRecordsStep.value];
    });

    const showStatusOptions = computed(() => {
      return noMedicalRecordsStep.value === 1;
    });

    return {
      selectedStatus,
      hospitalName,
      cityState,
      userEmail,
      userPassword,
      physicianName,
      validationSchema,
      registrationSchema,
      isFormFilled,
      isPasswordHidden,
      isConfirmationPasswordHidden,
      disableUserInputEmail,
      togglePassword,
      handleUploadMedicalRecords,
      onSubmitRegister,
      submitPhysicianForm,
      noMedicalRecordContent,
      showStatusOptions,
      description
    };
  },
};
